import type {
  MaintenanceItemAPI,
  MaintenanceRemindersSetupAPI,
  PastMaintenanceAPI,
  PastMaintenancesMapsAPI,
  RegisterMaintenanceAPI,
  UpcomingMaintenanceAPI,
  UpcomingMaintenancesByStatusAPI,
  VehicleMaintenanceRemindersSetupAPI,
  VehicleOdometerPost,
  VehicleRecurrencesAPI,
} from './APITypes';
import type {
  MaintenanceItem,
  MaintenanceRemindersSetup,
  PastMaintenance,
  PastMaintenancesMaps,
  Recurrence,
  RegisterPastMaintenance,
  ReminderType,
  UpcomingMaintenance,
  UpcomingMaintenanceByVehicleId,
  UpcomingMaintenancesByStatus,
  VehicleMaintenanceRemindersSetup,
} from './Types';

import type { UUID } from '@cobliteam/common-types';

import type { VehicleOdometer } from '../../__generated__/graphql';

export const toItem = (item: MaintenanceItemAPI): MaintenanceItem => ({
  id: item.item_id,
  name: item.item_name,
  price: item.item_price,
  remark: item.remark,
});

export const toItemAPI = (item: MaintenanceItem): MaintenanceItemAPI => ({
  item_id: item.id,
  item_name: item.name,
  item_price: item.price,
  remark: item.remark,
});

export const toPastMaintenance = (
  maintenance: PastMaintenanceAPI
): PastMaintenance => ({
  id: maintenance.id,
  vehicleId: maintenance.vehicle_id,
  date: maintenance.maintenance_time,
  totalCost: maintenance.total_price,
  items: maintenance.items?.map(toItem),
  odometerInMeters: maintenance.odometer,
  generalObservations: maintenance.general_remarks,
  types: maintenance.maintenance_type,
  periodicitiesInMeters: maintenance.recurrence_type,
  recurrenceNames: maintenance.recurrence_names,
});

export const toPastMaintenanceMaps = (
  maps: PastMaintenancesMapsAPI
): PastMaintenancesMaps => {
  const pastMaintenancesById: { [maintenanceId: UUID]: PastMaintenance } = {};
  const apiPastMaintenancesById = maps.pastMaintenancesById;

  Object.entries(apiPastMaintenancesById).forEach(
    ([maintenanceId, maintenancesApi]) => {
      pastMaintenancesById[maintenanceId] = toPastMaintenance(maintenancesApi);
    }
  );

  return {
    pastMaintenancesById,
    pastMaintenancesIdsByVehicleId: maps.pastMaintenancesIdsByVehicleId,
  };
};

export const toUpcomingMaintenance =
  (type: ReminderType) =>
  (m: UpcomingMaintenanceAPI): UpcomingMaintenance => ({
    metersDrivenSinceLastRevision: m.meters_driven_since_last_revision,
    revisionPeriodicityInMeters: m.revision_periodicity,
    lastRevisionDateInMillis: m.last_revision_date_in_millis,
    createdAt: m.created_at,
    revisionName: m.revision_periodicity_name,
    type,
  });

export const toUpcomingMaintenanceByVehicleId = (
  upcomingApi: { [vehicleId: UUID]: UpcomingMaintenanceAPI[] },
  type: ReminderType
) => {
  const upcomingMaintenancesByVehicleId: UpcomingMaintenanceByVehicleId = {};

  Object.entries(upcomingApi).forEach(([vehicleId, vehicleUpcomingApi]) => {
    upcomingMaintenancesByVehicleId[vehicleId] = vehicleUpcomingApi.map(
      toUpcomingMaintenance(type)
    );
  });

  return upcomingMaintenancesByVehicleId;
};

export const toUpcomingMaintenancesByStatus = (
  upcomingStatusApi: UpcomingMaintenancesByStatusAPI
): UpcomingMaintenancesByStatus => ({
  delayed: toUpcomingMaintenanceByVehicleId(
    upcomingStatusApi.delayed,
    'delayed'
  ),
  warning: toUpcomingMaintenanceByVehicleId(
    upcomingStatusApi.warning,
    'warning'
  ),
  onTime: toUpcomingMaintenanceByVehicleId(upcomingStatusApi.onTime, 'onTime'),
});

// This API interface supports multiple edits at once. This usage edits only one.
export const toVehicleRecurrencesAPI = (
  vehicleId: UUID,
  recurrences: Recurrence[]
): VehicleRecurrencesAPI[] => [
  {
    vehicle_id: vehicleId,
    recurrences_in_meters: recurrences.map(
      ({ recurrenceInKm }) => recurrenceInKm * 1000
    ),
    recurrences_names: recurrences
      .filter(({ recurrenceName }) => recurrenceName)
      .reduce(
        (acc, recurrence) => {
          const recurrenceInMeters = recurrence.recurrenceInKm * 1000;
          acc[recurrenceInMeters] = recurrence.recurrenceName || '';
          return acc;
        },
        {} as { [value: number]: string }
      ),
  },
];

export const toVehicleMaintenanceRemindersSetupAPI = (
  data: VehicleMaintenanceRemindersSetup
): VehicleMaintenanceRemindersSetupAPI => ({
  id: data.vehicleId,
  last_maintenance_date: data.lastMaintenanceDate,
  odometer_in_meters_at_that_date: data.odometerInMetersAtThatDate,
  current_odometer_in_meters: data.currentOdometerInMeters,
});

export const toMaintenanceRemindersSetupAPI = (
  data: MaintenanceRemindersSetup
): MaintenanceRemindersSetupAPI => ({
  vehicles_maintenance_setup: data.vehiclesMaintenanceSetup.map(
    toVehicleMaintenanceRemindersSetupAPI
  ),
  recurrence_in_meters: data.recurrenceInMeters,
  recurrence_names: data.recurrenceNames,
});

export const toPastMaintenanceAPI = (
  pm: RegisterPastMaintenance
): RegisterMaintenanceAPI => ({
  id: pm.id,
  vehicle_id: pm.vehicleId,
  maintenance_time: pm.date,
  odometer: pm.odometerInMeters,
  maintenance_type: pm.types,
  total_price: pm.totalCost,
  items: pm.items ? pm.items.map(toItemAPI) : undefined,
  general_remarks: pm.generalObservations,
  recurrence_type: pm.periodicitiesInMeters,
  recurrence_names: pm.recurrenceNames,
});

export const toVehicleOdometer = (
  vehicleOdometerPost: VehicleOdometerPost
): VehicleOdometer => ({
  ...vehicleOdometerPost,
  vehicleId: vehicleOdometerPost.vehicle.id,
  odometerInKm: vehicleOdometerPost.odometerStart,
  createdOdometerInKm: vehicleOdometerPost.odometerStart,
});
